import * as React from 'react'

type ButtonVariant = 'solid' | 'outline'
type ButtonColor = 'primary' | 'danger'
type ButtonSize = 'sm' | 'md'

interface TwButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  color?: ButtonColor
  size?: ButtonSize
  icon?: React.ReactNode
  children?: React.ReactNode
}

const baseClasses = 'items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border'

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2.5 text-sm',
}

const variantColorClasses: Record<ButtonVariant, Record<ButtonColor, string>> = {
  solid: {
    primary: 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-500 hover:border-indigo-500',
    danger: 'border-red-600 bg-red-600 text-white hover:bg-red-700 hover:border-red-700',
  },
  outline: {
    primary: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
    danger: 'border-red-300 bg-white text-red-600 hover:bg-red-50',
  },
}

export const TwButton: React.FC<TwButtonProps> = ({
  variant = 'solid',
  color = 'primary',
  size = 'sm',
  icon,
  children,
  className = '',
  ...props
}) => {
  // If className contains a display class (hidden, flex, inline-flex), don't add the default.
  // Exact-token match: a regex \b would falsely match utilities like `flex-1`.
  const displayClasses = ['hidden', 'flex', 'inline-flex', 'block', 'inline-block']
  const hasDisplayClass = className.split(/\s+/).some((cls) => displayClasses.includes(cls))
  const displayClass = hasDisplayClass ? '' : 'inline-flex'
  
  const classes = [
    displayClass,
    baseClasses,
    sizeClasses[size],
    variantColorClasses[variant][color],
    className,
  ].filter(Boolean).join(' ')

  return (
    <button type="button" className={classes} {...props}>
      {icon && <span className="h-5 w-5 flex items-center justify-center [&>svg]:h-5 [&>svg]:w-5">{icon}</span>}
      {children}
    </button>
  )
}
